const PerfMonitor = {
  marks: new Map(),
  
  start(name) {
    this.marks.set(name, performance.now());
  },
  
  end(name) {
    const start = this.marks.get(name);
    if (start) {
      const duration = performance.now() - start;
      console.info(`⚡ ${name}: ${duration.toFixed(2)}ms`);
      this.marks.delete(name);
      return duration;
    }
  }
};
function debounce(fn, delay = 300) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}
function throttle(fn, delay = 16) {
  let lastCall = 0;
  return (...args) => {
    const now = performance.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  };
}
const focusHandlers = {};

function trapFocus(container, firstFocus = null) {
  const focusable = container.querySelectorAll(
    'button, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  
  if (!focusable.length) return;
  
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  
  focusHandlers.keydown = (e) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey && e.target === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && e.target === last) {
      e.preventDefault();
      first.focus();
    }
  };
  
  container.addEventListener('keydown', focusHandlers.keydown);
  (firstFocus || first).focus();
}

function releaseFocus(container, returnFocus = null) {
  if (focusHandlers.keydown) {
    container.removeEventListener('keydown', focusHandlers.keydown);
    delete focusHandlers.keydown;
  }
  if (returnFocus) returnFocus.focus();
}
class Modal extends HTMLElement {
  constructor() {
    super();
    PerfMonitor.start('modal-init');
    this.boundHandleClick = this.handleClick.bind(this);
    this.addEventListener('click', this.boundHandleClick);
    PerfMonitor.end('modal-init');
  }

  handleClick(e) {
    if (e.target === this || e.target.matches('.close-modal')) {
      this.close();
    }
  }

  open(opener) {
    PerfMonitor.start('modal-open');
    
    this.scrollY = window.scrollY;
    document.body.style.cssText = `position: fixed; top: -${this.scrollY}px; width: 100%;`;
    
    this.setAttribute('open', '');
    this.openedBy = opener;
    
    trapFocus(this);
    
    this.escHandler = (e) => e.key === 'Escape' && this.close();
    document.addEventListener('keydown', this.escHandler);
    
    PerfMonitor.end('modal-open');
  }

  close() {
    PerfMonitor.start('modal-close');
    
    document.body.style.cssText = '';
    window.scrollTo(0, this.scrollY);
    
    this.removeAttribute('open');
    releaseFocus(this, this.openedBy);
    
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    
    PerfMonitor.end('modal-close');
  }
}
class Drawer extends HTMLElement {
  constructor() {
    super();
    PerfMonitor.start('drawer-init');
    this.overlay = document.querySelector('.overlay');
    this.boundHandleClick = this.handleClick.bind(this);
    PerfMonitor.end('drawer-init');
  }

  handleClick(e) {
    if (e.target.matches('.close-drawer') || e.target === this.overlay) {
      this.close();
    }
  }

  open(opener) {
    PerfMonitor.start('drawer-open');
    
    this.scrollY = window.scrollY;
    document.body.style.cssText = `position: fixed; top: -${this.scrollY}px; width: 100%;`;
    
    this.overlay?.classList.add('active');
    this.setAttribute('open', '');
    this.openedBy = opener;
    
    trapFocus(this);
    
    this.addEventListener('click', this.boundHandleClick);
    this.overlay?.addEventListener('click', this.boundHandleClick);
    
    this.escHandler = (e) => e.key === 'Escape' && this.close();
    document.addEventListener('keydown', this.escHandler);
    
    PerfMonitor.end('drawer-open');
  }

  close() {
    PerfMonitor.start('drawer-close');
    
    this.removeAttribute('open');
    this.overlay?.classList.remove('active');
    
    document.body.style.cssText = '';
    window.scrollTo(0, this.scrollY);
    
    releaseFocus(this, this.openedBy);
    
    this.removeEventListener('click', this.boundHandleClick);
    this.overlay?.removeEventListener('click', this.boundHandleClick);
    
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
    }
    
    PerfMonitor.end('drawer-close');
  }
}
class Carousel extends HTMLElement {
  constructor() {
    super();
    PerfMonitor.start('carousel-init');
    
    this.slides = this.querySelectorAll('.slide');
    if (this.slides.length < 2) return;
    
    this.container = this.querySelector('.slides-container');
    this.prevBtn = this.querySelector('.prev-btn');
    this.nextBtn = this.querySelector('.next-btn');
    
    this.currentIndex = 0;
    this.slideWidth = 0;
    
    this.init();
    PerfMonitor.end('carousel-init');
  }
  
  init() {
    this.updateSlideWidth();
    this.bindEvents();
    this.updateButtons();
    const resizeHandler = throttle(() => this.updateSlideWidth(), 100);
    window.addEventListener('resize', resizeHandler);
  }
  
  updateSlideWidth() {
    if (!this.container) return;
    this.slideWidth = this.slides[0].offsetWidth;
  }
  
  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updatePosition();
    }
  }
  
  next() {
    if (this.currentIndex < this.slides.length - 1) {
      this.currentIndex++;
      this.updatePosition();
    }
  }
  
  updatePosition() {
    const translateX = -this.currentIndex * this.slideWidth;
    this.container.style.transform = `translateX(${translateX}px)`;
    this.updateButtons();
  }
  
  updateButtons() {
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.slides.length - 1;
  }
}
class QuickAdd extends Drawer {
  constructor() {
    super();
    PerfMonitor.start('quickadd-init');
    
    this.cache = new Map();
    this.bindGlobalEvents();
    
    PerfMonitor.end('quickadd-init');
  }
  
  bindGlobalEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('.quick-add-btn')) {
        this.handleQuickAdd(e.target);
      }
    });
    document.addEventListener('mouseenter', (e) => {
      if (e.target.matches('.quick-add-btn') && e.target.dataset.productUrl) {
        this.prefetch(e.target.dataset.productUrl);
      }
    }, true);
  }
  
  async prefetch(url) {
    if (!this.cache.has(url)) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          this.cache.set(url, await response.text());
        }
      } catch (error) {
        console.warn('Prefetch failed:', error);
      }
    }
  }
  
  async handleQuickAdd(trigger) {
    PerfMonitor.start('quickadd-open');
    
    const productUrl = trigger.dataset.productUrl;
    if (!productUrl) return;
    
    trigger.disabled = true;
    
    try {
      let html = this.cache.get(productUrl);
      
      if (!html) {
        const response = await fetch(productUrl);
        html = await response.text();
        this.cache.set(productUrl, html);
      }
      
      this.renderProduct(html, productUrl);
      this.open(trigger);
      
    } catch (error) {
      console.error('Quick add failed:', error);
    } finally {
      trigger.disabled = false;
      PerfMonitor.end('quickadd-open');
    }
  }
  
  renderProduct(html, productUrl) {
    const template = document.createElement('template');
    template.innerHTML = html;
    
    const productData = template.content.querySelector('.product-form, [data-product-form]');
    if (!productData) return;
    
    this.innerHTML = `
      <div class="quick-add-content">
        <div class="quick-add-header">
          <h3>Quick Add</h3>
          <button class="close-drawer" aria-label="Close">×</button>
        </div>
        <div class="quick-add-body">
          ${productData.outerHTML}
        </div>
      </div>
    `;
  }
}
customElements.define('ui-modal', Modal);
customElements.define('ui-drawer', Drawer);
customElements.define('ui-carousel', Carousel);
customElements.define('ui-quickadd', QuickAdd);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    PerfMonitor.start('deferred-media-init');
    this.loaded = false;
    this.bindEvents();
    PerfMonitor.end('deferred-media-init');
  }

  bindEvents() {
    this.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent() {
    if (this.loaded) return;

    console.log('DeferredMedia loadContent called');
    PerfMonitor.start('deferred-media-load');
    
    const template = this.querySelector('template');
    console.log('Template found:', template);
    
    if (template) {
      const content = document.createElement('div');
      content.innerHTML = template.innerHTML;
      console.log('Template content:', template.innerHTML);
      
      // Replace template with actual content
      template.replaceWith(...content.childNodes);
      console.log('Template replaced with content');
      
      this.loaded = true;
      
      // Wait for DOM update before dispatching event
      requestAnimationFrame(() => {
        this.dispatchEvent(new CustomEvent('media:loaded', { 
          detail: { 
            videoElement: this.querySelector('.ugc-left-reviews-v2__video-element') 
          } 
        }));
        console.log('Media loaded event dispatched with video element');
      });
    }
    
    PerfMonitor.end('deferred-media-load');
  }
}

customElements.define('deferred-media', DeferredMedia);
document.addEventListener('shopify:section:load', () => {
  PerfMonitor.start('section-reload');
  document.querySelectorAll('ui-modal, ui-drawer, ui-carousel, ui-quickadd').forEach(el => {
    if (!el.initialized) {
      el.initialized = true;
    }
  });
  
  PerfMonitor.end('section-reload');
});
// Toggle/FAQ Universal Handler
const ToggleHandler = {
  init(container) {
    PerfMonitor.start('toggle-init');
    
    const toggles = container.querySelectorAll('[data-toggle]');
    
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const targetSelector = toggle.getAttribute('data-toggle');
        const toggleItem = toggle.closest('[data-toggle-item]') || toggle.closest('.faq-item, .toggle-item');
        const targetElement = targetSelector ? 
          document.querySelector(targetSelector) : 
          toggleItem?.querySelector('[data-toggle-content]');
        
        if (toggleItem && targetElement) {
          this.toggle(toggleItem, targetElement);
        }
      });
    });
    
    PerfMonitor.end('toggle-init');
  },
  
  toggle(item, content) {
    const isOpen = item.classList.contains('toggle-open') || item.classList.contains('faq-open');
    const openClass = item.classList.contains('faq-item') ? 'faq-open' : 'toggle-open';
    
    if (isOpen) {
      this.close(item, content, openClass);
    } else {
      this.open(item, content, openClass);
    }
  },
  
  open(item, content, openClass = 'toggle-open') {
    // Ajouter classe d'état ouvert d'abord pour appliquer les styles (padding, etc.)
    item.classList.add(openClass);
    
    // Calculer hauteur avec padding/margin inclus
    const contentHeight = content.scrollHeight + this.getExtraPadding(content);
    content.style.maxHeight = contentHeight + 'px';
    
    // Accessibilité
    const toggle = item.querySelector('[data-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  },
  
  close(item, content, openClass = 'toggle-open') {
    item.classList.remove(openClass);
    content.style.maxHeight = '0px';
    
    // Accessibilité
    const toggle = item.querySelector('[data-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  },
  
  getExtraPadding(element) {
    const computedStyle = getComputedStyle(element);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
    
    return paddingTop + paddingBottom + marginTop + marginBottom;
  }
};

const CartManager = {
  getSectionsToRender() {
    return [
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }
    ];
  },

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  },

  async addToCart(variantId, quantity = 1, properties = {}) {
    console.log('🛒 CartManager.addToCart called', { variantId, quantity, properties });
    PerfMonitor.start('cart-add');

    try {
      console.log('📝 Creating FormData...');

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);

      // Ajouter les propriétés
      if (properties && Object.keys(properties).length > 0) {
        Object.entries(properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });
      }

      // Ajouter les sections à rafraîchir (comme dans app.js)
      console.log('📦 Getting sections to render...');
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      const cartAddUrl = (typeof theme !== 'undefined' && theme.routes?.cart_add_url) || '/cart/add';
      console.log('🌐 Cart add URL:', cartAddUrl);

      console.log('🚀 Sending fetch request...');
      const response = await fetch(cartAddUrl, config);

      console.log('📥 Response received:', response.status, response.statusText);

      if (!response.ok) {
        console.error('❌ Response not OK');
        const error = await response.json();
        console.error('❌ Error response:', error);
        throw new Error(error.description || error.message || 'Failed to add to cart');
      }

      console.log('📄 Parsing JSON response...');
      const data = await response.json();
      console.log('✅ Response data:', data);

      // Vérifier s'il y a une erreur status dans la réponse
      if (data.status) {
        console.error('❌ Response has error status');
        throw new Error(data.description || data.message || 'Failed to add to cart');
      }

      // Rafraîchir le cart drawer avec les nouvelles sections
      console.log('🔄 Rendering cart sections...');
      this.renderCartSections(data);

      // Ouvrir le cart drawer
      console.log('📂 Opening cart drawer...');
      this.openCartDrawer();

      // Dispatcher un événement personnalisé (comme dans app.js)
      console.log('📢 Dispatching cart:item-added event...');
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: {
          product: data.hasOwnProperty('items') ? data.items[0] : data
        }
      }));

      PerfMonitor.end('cart-add');
      console.log('✅ Cart add completed successfully');
      return { success: true, data };

    } catch (error) {
      console.error('❌ Error adding to cart:', error);
      console.error('❌ Error stack:', error.stack);
      PerfMonitor.end('cart-add');
      return { success: false, error: error.message };
    }
  },

  renderCartSections(parsedState) {
    if (!parsedState.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) return;

      const elementToReplace = element.querySelector(section.selector) || element;

      if (parsedState.sections[section.section]) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }

      // Réinitialiser les événements du cart drawer
      if (section.id === 'Cart-Drawer') {
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (cartDrawer) {
          cartDrawer.notesToggle?.();
          cartDrawer.removeProductEvent?.();
        }
      }
    });
  },

  async refreshCart() {
    try {
      const rootUrl = (typeof theme !== 'undefined' && theme.routes?.root_url) || '/';
      const response = await fetch(rootUrl + 'cart.js');
      const cart = await response.json();

      // Dispatcher un événement
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart }
      }));

      return cart;
    } catch (error) {
      console.error('Error refreshing cart:', error);
      return null;
    }
  },

  async getCart() {
    try {
      const rootUrl = (typeof theme !== 'undefined' && theme.routes?.root_url) || '/';
      const response = await fetch(rootUrl + 'cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error getting cart:', error);
      return null;
    }
  },

  openCartDrawer() {
    console.log('📂 openCartDrawer called');

    // Utiliser le nouveau w-sidecart si disponible
    if (typeof window.openSidecart === 'function') {
      console.log('✅ Opening w-sidecart');
      window.openSidecart();
      return;
    }

    // Fallback vers l'ancien cart-drawer
    const cartDrawer = document.querySelector('cart-drawer') || document.getElementById('Cart-Drawer');
    console.log('🎯 Cart drawer element:', cartDrawer);

    if (cartDrawer) {
      // Si c'est un custom element avec une méthode open()
      if (typeof cartDrawer.open === 'function') {
        console.log('✅ Using custom element open() method');
        cartDrawer.open();
      } else {
        // Sinon utiliser les classes CSS
        console.log('✅ Using CSS classes to open drawer');
        cartDrawer.classList.add('active');
        document.body.classList.add('open-cart');
        document.body.classList.add('open-cc');

        // Activer les product recommendations si présentes
        setTimeout(() => {
          const recommendations = cartDrawer.querySelector('.product-recommendations--full');
          if (recommendations) {
            recommendations.classList.add('active');
          }
        }, 100);
      }

      // Dispatcher l'événement
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    } else {
      console.error('❌ Cart drawer element not found');
    }
  }
};

window.Pleiades = {
  perf: PerfMonitor,
  debounce,
  throttle,
  trapFocus,
  releaseFocus,
  Modal,
  Drawer,
  Carousel,
  QuickAdd,
  ToggleHandler,
  CartManager
};

if (window.location.search.includes('debug=performance')) {
  console.info('🚀 Pleiades Performance Mode Enabled');
  const originalEnd = PerfMonitor.end;
  PerfMonitor.end = function(name) {
    const duration = originalEnd.call(this, name);
    if (duration > 16) {
      console.warn(`⚠️ Slow operation: ${name} took ${duration.toFixed(2)}ms`);
    }
    return duration;
  };
}